import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './components/auth/AuthProvider';
import Navbar from './components/Navbar';
import LoadingSpinner from './components/LoadingSpinner';
import ErrorBoundary from './components/ErrorBoundary';
import PrivateRoute from './components/auth/PrivateRoute';
import Footer from './components/Footer';
// import DebugDataLoader from './components/debug/DebugDataLoader';
import InstallPrompt from './components/InstallPrompt';
import OfflineIndicator from './components/OfflineIndicator';
import useMigrateTestHistory from './hooks/useMigrateTestHistory';
import { cleanAuthStorage } from './services/api/config';

const Home = lazy(() => import('./pages/Home'));
const Test = lazy(() => import('./pages/Test'));
const TestDebug = lazy(() => import('./pages/TestDebug')); // Add Test Debug component
const Results = lazy(() => import('./pages/Results'));
const Login = lazy(() => import('./pages/Login'));
const Signup = lazy(() => import('./pages/Signup'));
const Profile = lazy(() => import('./pages/Profile'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
// Use components from the admin directory
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const ModuleManagement = lazy(() => import('./pages/admin/EnhancedModuleManagement'));
// Use our updated UserManagement component from the correct path
const UserManagement = lazy(() => import('./pages/admin/UserManagement'));
const Analytics = lazy(() => import('./pages/admin/Analytics'));
const Settings = lazy(() => import('./pages/admin/Settings'));
const EmailManagement = lazy(() => import('./pages/admin/EmailManagement'));
const MediaLibrary = lazy(() => import('./pages/admin/MediaLibrary'));

const AuthDebug = lazy(() => import('./pages/AuthDebug'));
// New Learning Module pages
const LearningModules = lazy(() => import('./pages/LearningModules'));
const ModuleDetail = lazy(() => import('./pages/ModuleDetail'));

const AppContent = () => {
  // Use the migration hook
  useMigrateTestHistory();
  
  // Clean up malformed auth data on app load
  useEffect(() => {
    cleanAuthStorage();
  }, []);
  
  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-indigo-50 to-purple-50">
      {/* <DebugDataLoader /> */}
      <Navbar />
      <main className="flex-grow">
        <Suspense fallback={<LoadingSpinner />}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/test" element={<Test />} />
            <Route path="/test-debug" element={<TestDebug />} />
            <Route path="/results" element={<Results />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/profile" element={<Profile />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/auth-debug" element={<AuthDebug />} />
            
            {/* Learning Module Routes */}
            <Route path="/learning" element={<LearningModules />} />
            <Route path="/module/:moduleId" element={<ModuleDetail />} />
            
            {/* Admin Routes */}
            <Route
              path="/admin"
              element={
                <PrivateRoute adminOnly={true}>
                  <AdminDashboard />
                </PrivateRoute>
              }
            />
            <Route 
              path="/admin/tests" 
              element={
                <PrivateRoute adminOnly={true}>
                  <AdminDashboard />
                </PrivateRoute>
              } 
            />
            <Route 
              path="/admin/modules" 
              element={
                <PrivateRoute adminOnly={true}>
                  <ModuleManagement />
                </PrivateRoute>
              } 
            />
            <Route 
              path="/admin/users" 
              element={
                <PrivateRoute adminOnly={true}>
                  <UserManagement />
                </PrivateRoute>
              } 
            />
            <Route 
              path="/admin/media" 
              element={
                <PrivateRoute adminOnly={true}>
                  <MediaLibrary />
                </PrivateRoute>
              } 
            />
            <Route 
              path="/admin/analytics" 
              element={
                <PrivateRoute adminOnly={true}>
                  <Analytics />
                </PrivateRoute>
              } 
            />
            <Route 
              path="/admin/settings" 
              element={
                <PrivateRoute adminOnly={true}>
                  <Settings />
                </PrivateRoute>
              } 
            />
            <Route 
              path="/admin/emails" 
              element={
                <PrivateRoute adminOnly={true}>
                  <EmailManagement />
                </PrivateRoute>
              } 
            />

          </Routes>
        </Suspense>
      </main>
      <Footer />
      <InstallPrompt />
      <OfflineIndicator />
    </div>
  );
};

const App = () => {
  return (
    <ErrorBoundary>
      <Router>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </Router>
    </ErrorBoundary>
  );
};

export default App;